import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Container, CircularProgress, Chip } from "@mui/material";
import { Typography, TextField, Autocomplete } from "@mui/material";
import { filterOptions, useDebounce, useViewport } from "../../helpers";
import {
  myDesignService,
  myExpariencesService,
  userService,
} from "../../services";
import MyMapContainer from "../geoloc/MyMapContainer";
import UserAuthorizationsManagement from "./UserAuthorizationsManagement";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import { useSnackbar } from "notistack";

const PREFIX = "PrivacyOptions";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  stepperButtonPositions: `${PREFIX}-stepperButtonPositions`,
  marginLeft: `${PREFIX}-marginLeft`,
  paper: `${PREFIX}-paper`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem !important",
  },

  [`& .${classes.stepperButtonPositions}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
  },

  [`& .${classes.marginLeft}`]: {
    marginLeft: "1rem",
  },

  [`& .${classes.paper}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.between("xs", "md")]: {
      maxWidth: "350px",
    },
    width: "100%",
    margin: "0 auto",
    padding: "2rem 0",
  },
}));

const PrivacyOptions = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [geolocalisation, setGeolocalisation] = useState(
    props.geolocalisation ? props.geolocalisation : false
  );
  const [positions, setPositions] = useState(
    props.positions ? props.positions : []
  );
  const [selectedChip, setSelectedChip] = useState(null);

  const [fetching, setFetching] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const debouncedInput = useDebounce(inputValue, 500);

  const toastError = () =>
    enqueueSnackbar(t("toasts.errorUpdateUsers"), { variant: "error" });

  useEffect(() => {
    myDesignService
      .getAuthorizedAccounts(props.frameId)
      .then((res) => setSelectedUsers(res));
  }, []);

  useEffect(() => {
    if (inputValue.length > 0) {
      setFetching(true);
      userService
        .searchUser(inputValue)
        .then((res) => {
          setFetchedUsers(res);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        })
        .finally(() => setFetching(false));
    }
  }, [debouncedInput]);

  return (
    <Container disableGutters>
      <Root>
        <ResponsiveDialogManager
          open={selectedChip ? true : false}
          disableGutters
          fullScreen
          renderer={
            <UserAuthorizationsManagement
              frameId={props.frameId}
              selectedChip={selectedChip}
            />
          }
          title={`${t("myDesign.privacy.account")}: ${
            selectedChip && selectedChip.login
          } `}
          onClose={() => setSelectedChip(null)}
        />
        <div
          style={{
            maxWidth: width > 1024 ? "80%" : "inherit",
            margin: "0 auto",
            marginBottom: "1rem",
          }}
        >
          <Typography variant="h5" className={classes.marginBottom}>
            {t("homePage.myDesign.createForm.associateUsers")}
          </Typography>
          <Autocomplete
            label={t("homePage.myDesign.createForm.associateUsers")}
            id="autocomplete-users-create-desin"
            disableClearable
            multiple
            loading={fetching}
            options={fetchedUsers}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.login}
            defaultValue={selectedUsers}
            value={selectedUsers}
            inputValue={inputValue}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            onChange={(e, value, reason, detail) => {
              if (reason === "removeOption") {
                myDesignService
                  .deleteAccountAuthorization(
                    props.frameId,
                    detail.option.id,
                    toastError
                  )
                  .then(setSelectedUsers(value));
              } else if (reason === "selectOption") {
                setInputValue("");
                myDesignService
                  .addAccountAuthorization(
                    props.frameId,
                    detail.option.id,
                    toastError
                  )
                  .then(setSelectedUsers(value));
              }
            }}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={option.login}
                  onClick={() => setSelectedChip(option)}
                />
              ));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("homePage.myDesign.createForm.searchUser")}
                variant="outlined"
                onChange={(e) => setInputValue(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {fetching ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          {/* <FormControlLabel
            control={
              <Checkbox
                checked={geolocalisation}
                color="primary"
                onChange={() => setGeolocalisation(!geolocalisation)}
                name={t('homePage.myDesign.createForm.geoloc')}
              />}
            label={t('homePage.myDesign.createForm.geoloc')}
            style={{marginTop:'1rem'}}
          />
          {geolocalisation &&
            <MyMapContainer positions={positions} save={setPositions} />
          } */}
        </div>
        {/* <Divider light className={classes.marginBottom}/>
        <div className={classes.stepperButtonPositions}>
          <Button
            disabled={props.activeStep === 0}
            onClick={props.handleBack}
            className={classes.button}
          >
            {t('homePage.myDesign.createForm.back')}
          </Button>
          <Button
          color="primary"
          variant="contained"
          onClick={() => handleSubmit({users: selectedUsers, geoloc: geolocalisation, positions})}
          disabled={geolocalisation && positions.length === 0}
          className={clsx(classes.marginLeft)}
        >
          {t('homePage.myDesign.createForm.submit')}
        </Button>
        </div> */}
      </Root>
    </Container>
  );
};

export default PrivacyOptions;
